import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosClientAdmin } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MonitoringTeam } from './types';
import { createCompanyTenantMonitoring } from './teamsApi';
import { useAuthStore } from '@/store/authStore';
import axios from 'axios';

type CreateTeamPayload = Pick<MonitoringTeam, 'name' | 'primaryEmail' | 'domain'>;

// Wrapper to call createCompanyTenantMonitoring with tenantId from auth store
async function createMonitoringTeam(teamData: CreateTeamPayload, tenantId: string) {
  return createCompanyTenantMonitoring({ tenantId, body: teamData });
}

export function AddTeamPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const { tenant } = useAuthStore();
  const tenantId = tenant?.tenantId || '';

  const mutation = useMutation({
    mutationFn: (teamData: CreateTeamPayload) => createMonitoringTeam(teamData, tenantId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['monitoringTeams'] });
      // Navigate to the newly created team's detail page
      const id = data?.monitoringCompany?.id;
      if (id) {
        navigate(`/tenant/${tenantId}/team/${id}`);
      } else {
        // Fallback if ID is somehow not present, though it should be
        console.warn('Team created but ID not found in response, navigating to list.');
        navigate('/team');
      }
      console.log('Team created successfully:', data);
      // TODO: Add success notification (e.g., toast)
    },
    onError: (error: any) => {
      if (axios.isAxiosError(error) && error.response) {
        setSubmissionError(
          error.response.data?.message || `Server error: ${error.response.status}`
        );
      } else {
        setSubmissionError(error.message || 'An unexpected error occurred.');
      }
      console.error('Error creating team:', error);
      // TODO: Add error notification (e.g., toast if a toast system is in place)
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmissionError(null); // Clear previous errors
    if (!name || !primaryEmail) {
      // Basic validation, can be improved with a library like Zod
      alert('Name and Primary Email are required.');
      return;
    }
    const domainPart = primaryEmail.split('@')[1];
    const emailDomain = domainPart ? `@${domainPart}` : ''; // Extract domain from email and prefix with @
    mutation.mutate({ name, primaryEmail, domain: emailDomain });
  };

  return (
    <div className="container mx-auto p-4">
      <Card className="mx-auto max-w-2xl" >
        <CardHeader data-onboarding="add-team-header">
          <CardTitle>Add New Monitoring Team</CardTitle>
          <CardDescription>Enter the details for the new monitoring team.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit} data-onboarding="add-team-form">
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Team Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter team name"
                required
                data-onboarding="add-team-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryEmail">Primary Email</Label>
              <Input
                id="primaryEmail"
                type="email"
                value={primaryEmail}
                onChange={(e) => setPrimaryEmail(e.target.value)}
                placeholder="Enter primary email"
                required
                data-onboarding="add-team-email"
              />
            </div>
            {submissionError && <p className="text-sm text-red-500">{submissionError}</p>}
          </CardContent>
          <CardFooter className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => navigate('/team')}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-onboarding="add-team-create-button">
              {mutation.isPending ? 'Creating...' : 'Create Team'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

export default AddTeamPage;
